import { z } from 'zod';

// Contrato de fio do Split automático (U27), lido do legado (`UYZY@master`):
// `services/automaticSplit/automaticSplit.ts` + `automaticSplit.types.ts`. Dinheiro em centavos
// inteiros (`formatCurrencyInCents`), percentual como fração (`formatPercentage` multiplica por 100).

/** Id do registro novo na URL do legado (`newId`, `utils/constants/automaticSplit/automaticSplit.ts:16`). */
export const NEW_AUTOMATIC_SPLIT_ID = 'nova';

/** `IAutomaticSplitListReadModel` (`automaticSplit.types.ts:58-71`) + `DefaultRecordDetails`. */
export const automaticSplitListItemSchema = z.looseObject({
  id: z.string(),
  creditors: z.array(z.string()).nullish(),
  borrowerName: z.string().nullish(),
  principalAmount: z.number().nullish(),
  outstandingBalance: z.number().nullish(),
  escrowBalance: z.number().nullish(),
  installmentsCount: z.number().nullish(),
  openInstallments: z.number().nullish(),
  guaranteePercentOverPMT: z.number().nullish(),
  status: z.number().nullish(),
  statusDescription: z.string().nullish(),
  createdAt: z.string().nullish(),
});
export type AutomaticSplitListItem = z.infer<typeof automaticSplitListItemSchema>;

/** A lista vem crua (`items`, `total`); o legado a remonta em `data`/`totalItems` (`automaticSplit.ts:196-205`). */
export const automaticSplitListResponseSchema = z.looseObject({
  items: z.array(automaticSplitListItemSchema).nullish(),
  total: z.number().nullish(),
  page: z.number().nullish(),
  totalPages: z.number().nullish(),
});
export type AutomaticSplitListResponse = z.infer<typeof automaticSplitListResponseSchema>;

/** Filtros do legado (`IAutomaticSplitListFilters` + `f_account` de `AutomaticSplitListContainer.tsx:78-95`). */
export interface AutomaticSplitListParams {
  readonly page: number;
  readonly size: number;
  /** Na ordem em que a pessoa aplicou (`filterContext`). */
  readonly filters?: Readonly<Record<string, unknown>>;
}

/**
 * Query de `GET /BankAccount/AutomaticSplit/Operations`: `{ tenant, page, size, ...filtros }`
 * (`automaticSplitContext.tsx:132-134`). O `tenant` entra pelo `withTenant` do cliente, primeiro.
 */
export function automaticSplitListQueryParams(params: AutomaticSplitListParams): Record<string, unknown> {
  return { page: params.page, size: params.size, ...params.filters };
}

/** Transferência de uma parcela (lida em `AutomaticSplitPlanContainer.tsx:72-86`). */
export const automaticSplitTransferSchema = z.looseObject({
  id: z.string(),
  amount: z.number().nullish(),
  transferDate: z.string().nullish(),
  status: z.string().nullish(),
  bankAccountBeneficiary: z.looseObject({ id: z.string().nullish(), name: z.string().nullish() }).nullish(),
});
export type AutomaticSplitTransfer = z.infer<typeof automaticSplitTransferSchema>;

/** Parcela do plano (`installments` do `GET`, colunas de `AutomaticSplitPlanList.tsx:35-169`). */
export const automaticSplitInstallmentSchema = z.looseObject({
  id: z.union([z.string(), z.number()]).nullish(),
  number: z.number(),
  dueDate: z.string().nullish(),
  amount: z.number().nullish(),
  paidAmount: z.number().nullish(),
  interestAccrued: z.number().nullish(),
  fineAccrued: z.number().nullish(),
  paymentDate: z.string().nullish(),
  status: z.number().nullish(),
  bankAccountTransfers: z.array(automaticSplitTransferSchema).nullish(),
});
export type AutomaticSplitInstallment = z.infer<typeof automaticSplitInstallmentSchema>;

const party = z.looseObject({
  id: z.string().nullish(),
  name: z.string().nullish(),
  taxId: z.string().nullish(),
  account: z.union([z.string(), z.number()]).nullish(),
  pixKey: z.string().nullish(),
  payoutRail: z.union([z.string(), z.number()]).nullish(),
});

/** `GET /BankAccount/AutomaticSplit/Operation/{id}` cru (`automaticSplit.ts:119-185`). */
export const automaticSplitOperationSchema = z.looseObject({
  id: z.string(),
  createdAt: z.string().nullish(),
  principalAmount: z.number().nullish(),
  installmentsCount: z.number().nullish(),
  firstDueDate: z.string().nullish(),
  guaranteePercentOverPMT: z.number().nullish(),
  interestPercent: z.number().nullish(),
  finePercent: z.number().nullish(),
  borrowerTransferAuthorized: z.boolean().nullish(),
  borrowerTransferSameDay: z.boolean().nullish(),
  borrowerTransferDayOfMonth: z.number().nullish(),
  acceptPartialInstallment: z.boolean().nullish(),
  escrowBankAccountId: z.string().nullish(),
  borrowerBeneficiaryId: z.string().nullish(),
  status: z.number().nullish(),
  escrow: z.looseObject({ accountNumber: z.union([z.string(), z.number()]).nullish() }).nullish(),
  borrower: party.nullish(),
  creditors: z.array(party).nullish(),
  installments: z.array(automaticSplitInstallmentSchema).nullish(),
});
export type AutomaticSplitOperation = z.infer<typeof automaticSplitOperationSchema>;

/** Favorecido da conta escrow (`IAutomaticSplitEscrowAccountBeneficiary`, `automaticSplit.types.ts:81-95`). */
export const escrowBeneficiarySchema = z.looseObject({
  name: z.string().nullish(),
  registrationNumber: z.string().nullish(),
  bank: z.union([z.number(), z.string()]).nullish(),
  bankDisplay: z.string().nullish(),
  bankName: z.string().nullish(),
  agency: z.union([z.number(), z.string()]).nullish(),
  accountNumber: z.union([z.string(), z.number()]).nullish(),
  pixKeyTypeValue: z.string().nullish(),
  pixKeyTypeValueDisplay: z.string().nullish(),
  keyPix: z.string().nullish(),
  beneficiaryIdPIX: z.string().nullish(),
  beneficiaryIdTED: z.string().nullish(),
  type: z.string().nullish(),
  proportion: z.number().nullish(),
  paymentType: z.number().nullish(),
});
export type EscrowBeneficiary = z.infer<typeof escrowBeneficiarySchema>;

/** `IAutomaticSplitResponseEscrowBeneficiary` (`automaticSplit.types.ts:97-105`). */
export const escrowBeneficiariesResponseSchema = z.looseObject({
  bankAccountId: z.string().nullish(),
  bankCode: z.union([z.number(), z.string()]).nullish(),
  agency: z.union([z.number(), z.string()]).nullish(),
  accountNumber: z.union([z.string(), z.number()]).nullish(),
  accountType: z.string().nullish(),
  accountTypeDescription: z.string().nullish(),
  beneficiaries: z.array(escrowBeneficiarySchema).nullish(),
});
export type EscrowBeneficiariesResponse = z.infer<typeof escrowBeneficiariesResponseSchema>;

/** Resposta do `POST …/Operation/Simulate`: o plano (`automaticSplit.ts:79-84`). */
export const simulateResponseSchema = z.looseObject({
  plan: z.array(z.looseObject({ number: z.number(), dueDate: z.string().nullish(), amount: z.number().nullish() })),
});
export type SimulateResponse = z.infer<typeof simulateResponseSchema>;

/** Corpo do `POST …/Installment/{installmentId}/installmentStatus` (`automaticSplit.ts:259-265`). */
export interface InstallmentStatusBody {
  readonly newStatus: number;
}

/** Corpo do `POST …/Operation/{id}/Cancel` (`IAutomaticSplitCancel`, `automaticSplit.types.ts:117-120`). */
export interface CancelBody {
  readonly code: string;
  readonly sessionId: string;
}

/** Credor do corpo de criar/editar (`getCreditorsFromBeneficiaries`, `automaticSplit.ts:47-51`). */
export interface CreditorBody {
  readonly beneficiaryId: string | null | undefined;
  readonly proportion: number;
  readonly active: true;
}

/**
 * Corpo de criar (`POST …/Operation`) e editar (`PUT …/Operation/{id}`): o formulário inteiro do
 * legado, espalhado, mais os quatro campos calculados (`automaticSplit.ts:98-110`).
 */
export type SaveBody = Record<string, unknown> & {
  readonly firstDueDate: string | null;
  readonly escrowBankAccountId: unknown;
  readonly borrowerBeneficiaryId: string | null | undefined;
  readonly creditors: readonly CreditorBody[];
  readonly planOverride: readonly Record<string, unknown>[] | undefined;
};

/** Parâmetros do `GET …/Escrow/Beneficiaries` (`automaticSplit.ts:210-227`). */
export type EscrowQuery = Record<string, unknown>;
